'use client';

import React, { type ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Card, ClassesLoadError, EmptyCalendarState } from '@/components';
import { Button, Separator, Skeleton } from '@mokoboko/components';
import { ChevronRight } from 'lucide-react';
import { Illustration } from '@/components/illustration';
import { useDetailHref } from '@/contexts/embed';
import { classCardFlagsOf } from '@/helpers/class';
import { OFFERING_LINKS } from '@/helpers/offering';
import { cardImageOf, focalPointToObjectPosition } from '@/lib/entity-image';
import type { IllustrationName } from '@/lib/illustrations';
import { mergeClassNames } from '@/lib/utils';
import { AppearanceSettings } from '@/state/appearance-settings';
import { Class } from '@/state/class-groups';
import {
  useClassAvailability,
  useClassPackAvailability,
  useGiftCardAvailability,
  useSubscriptionAvailability,
} from '@/hooks/use-offering-availability';

// How many rows exist is the very thing being fetched; three is the common
// case.
const SKELETON_ROW_COUNT = 3;

// On phones the rows read as one square card split by rules; from sm each row
// is a card of its own, rounded like the studio header above it.
const CATEGORY_STACK = 'flex flex-col shadow-xs sm:gap-3 sm:shadow-none';
const CATEGORY_ROW_CARD =
  'max-sm:rounded-none max-sm:shadow-none sm:rounded-2xl';
const CATEGORY_ROW_CONTENT = 'flex items-center justify-between gap-4 p-5';
const ROW_CHEVRON_MOTION =
  'transition-transform duration-200 group-hover:translate-x-1';
// The name's link covers the row, so the row is one tab stop and a real link.
// The ring sits inside because the card crops anything outside it.
const ROW_LINK =
  'min-w-0 outline-none after:absolute after:inset-0 focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-red-400 sm:after:rounded-2xl';

const RowRule = () => (
  <div className="bg-white px-5 sm:hidden">
    <Separator />
  </div>
);

const RowStack = ({ children }: { children: ReactNode }) => (
  <div className={CATEGORY_STACK}>
    {React.Children.map(children, (row, index) => (
      <>
        {index > 0 && <RowRule />}
        {row}
      </>
    ))}
  </div>
);

const BookingCategoryGroupsSkeleton = () => (
  <RowStack>
    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
      <Card
        key={i}
        className={CATEGORY_ROW_CARD}
        contentClassName={CATEGORY_ROW_CONTENT}
      >
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <Skeleton
            isCircle
            height={56}
            className="order-last shrink-0 sm:order-first"
          />
          <div className="flex flex-1 flex-col gap-1.5 sm:gap-0.5">
            <Skeleton text="body-lg" width={112} />
            <Skeleton text="body-sm" width="100%" className="max-w-80" />
          </div>
        </div>
        <Skeleton
          isRounded
          height={40}
          width={128}
          className="shrink-0 max-sm:hidden"
        />
      </Card>
    ))}
  </RowStack>
);

const RowIllustration = ({ name }: { name: IllustrationName }) => (
  <Illustration
    name={name}
    alt=""
    width={112}
    height={112}
    quality={100}
    className="order-last size-14 shrink-0 object-contain sm:order-first"
  />
);

const BookingRow = ({
  name,
  description,
  href,
  media,
}: {
  name: string;
  description: string | null;
  href: string;
  media: ReactNode;
}) => {
  const router = useRouter();

  return (
    <Card
      className={mergeClassNames(
        'group relative transition-shadow duration-200 md:hover:shadow-lg',
        CATEGORY_ROW_CARD
      )}
      contentClassName={CATEGORY_ROW_CONTENT}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        {media}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-0.5">
          <p className="flex items-center gap-1 font-display text-lg font-bold text-gray-900 transition-colors duration-200 group-hover:text-red-500">
            <Link href={href} className={ROW_LINK}>
              {name}
            </Link>
            <ChevronRight
              aria-hidden
              className={mergeClassNames(
                'size-5 shrink-0 text-red-500 sm:hidden',
                ROW_CHEVRON_MOTION
              )}
            />
          </p>
          {description && (
            <p className="text-sm leading-5 text-gray-600">{description}</p>
          )}
        </div>
      </div>
      {/* Above the link for its own hover; the keyboard stops on the link. */}
      <Button
        variant="secondary-gray"
        trailingIcon={<ChevronRight className={ROW_CHEVRON_MOTION} />}
        className="relative z-10 max-sm:hidden"
        tabIndex={-1}
        aria-hidden
        onClick={() => router.push(href)}
      >
        Select
      </Button>
    </Card>
  );
};

// The list's own switches decide the image and the description here too, and a
// class with no image to show keeps the calendar.
const ClassRow = ({
  classData,
  classSettings,
}: {
  classData: Class;
  classSettings: AppearanceSettings['class'] | undefined;
}) => {
  const href = useDetailHref('class', classData.class_id, classData.slug);
  const { showImage, showDescription } = classCardFlagsOf(classSettings);
  const { url: imageUrl, focalX, focalY } = cardImageOf(classData);

  return (
    <BookingRow
      name={classData.name}
      description={showDescription ? classData.short_description : null}
      href={href}
      media={
        showImage && imageUrl ? (
          <div className="relative order-last size-14 shrink-0 overflow-hidden rounded-xl sm:order-first">
            <Image
              src={imageUrl}
              alt=""
              fill
              sizes="56px"
              className="object-cover"
              style={{
                objectPosition: focalPointToObjectPosition(focalX, focalY),
              }}
            />
          </div>
        ) : (
          <RowIllustration name="calendar-3d" />
        )
      }
    />
  );
};

type BookingCategory = {
  name: string;
  description: string;
  href: string;
  illustration: IllustrationName;
};

const CLASSES_CATEGORY: BookingCategory = {
  href: OFFERING_LINKS.classes.href,
  illustration: 'calendar-3d',
  name: 'Classes',
  description: 'Pick a date and book a single class',
};
const CLASS_PACKS_CATEGORY: BookingCategory = {
  ...OFFERING_LINKS.classPacks,
  name: 'Class packs',
  description: 'A set of classes to book when you want',
};
const MEMBERSHIPS_CATEGORY: BookingCategory = {
  ...OFFERING_LINKS.subscriptions,
  name: 'Memberships',
  description: 'A plan that renews automatically',
};
const GIFT_CARDS_CATEGORY: BookingCategory = {
  href: '/gift-cards',
  illustration: 'gift-card-3d',
  name: 'Gift cards',
  description: 'Give someone classes as a gift',
};

// classSettings is undefined for a studio whose settings did not load; the
// page still shows what it sells, on the cards' own defaults.
export const BookingCategoryGroups = ({
  classSettings,
}: {
  classSettings: AppearanceSettings['class'] | undefined;
}) => {
  const {
    classes,
    hasClasses,
    isLoading: isClassGroupsLoading,
    error: classesError,
  } = useClassAvailability();
  const { hasClassPacks, isLoading: isClassPackGroupsLoading } =
    useClassPackAvailability();
  const { hasSubscriptions, isLoading: isSubscriptionGroupsLoading } =
    useSubscriptionAvailability();
  const { hasGiftCards, isLoading: isGiftCardsLoading } =
    useGiftCardAvailability();

  if (
    isClassGroupsLoading ||
    isClassPackGroupsLoading ||
    isSubscriptionGroupsLoading ||
    isGiftCardsLoading
  ) {
    return <BookingCategoryGroupsSkeleton />;
  }

  if (!hasClasses && !hasClassPacks && !hasSubscriptions && !hasGiftCards) {
    return (
      <Card className="max-sm:rounded-none" contentClassName="p-5">
        <EmptyCalendarState
          className="py-6"
          title="Nothing available yet"
          description="This studio has not published any classes, packs or memberships yet."
        />
      </Card>
    );
  }

  // Only a studio selling nothing else lists its classes here. One pack,
  // membership or gift card on sale, and each product is one category.
  const sellsOnlyClasses =
    hasClasses && !hasClassPacks && !hasSubscriptions && !hasGiftCards;

  if (sellsOnlyClasses && classesError) {
    return (
      <Card className="max-sm:rounded-none" contentClassName="p-5">
        <ClassesLoadError />
      </Card>
    );
  }

  if (sellsOnlyClasses) {
    return (
      <RowStack>
        {classes.map((classData) => (
          <ClassRow
            key={classData.class_id}
            classData={classData}
            classSettings={classSettings}
          />
        ))}
      </RowStack>
    );
  }

  const categories = [
    ...(hasClasses ? [CLASSES_CATEGORY] : []),
    ...(hasClassPacks ? [CLASS_PACKS_CATEGORY] : []),
    ...(hasSubscriptions ? [MEMBERSHIPS_CATEGORY] : []),
    ...(hasGiftCards ? [GIFT_CARDS_CATEGORY] : []),
  ];

  return (
    <RowStack>
      {categories.map((category) => (
        <BookingRow
          key={category.name}
          name={category.name}
          description={category.description}
          href={category.href}
          media={<RowIllustration name={category.illustration} />}
        />
      ))}
    </RowStack>
  );
};
